import { __awaiter, __generator } from "tslib";
import { getAnalyticsConnector, getGlobalScope } from '@amplitude/analytics-client-common';
import { Logger, returnWrapper } from '@amplitude/analytics-core';
import { pack, record } from '@amplitude/rrweb';
import { SessionReplayConfig } from './config';
import { BLOCK_CLASS, DEFAULT_SESSION_REPLAY_PROPERTY, MASK_TEXT_CLASS, SESSION_REPLAY_DEBUG_PROPERTY, } from './constants';
import { SessionReplayEventsManager } from './events-manager';
import { generateHashCode, generateSessionReplayId, isSessionInSample, maskInputFn } from './helpers';
import { SessionIdentifiers } from './identifiers';
var SessionReplay = /** @class */ (function () {
    function SessionReplay() {
        var _this = this;
        this.name = '@amplitude/session-replay-browser';
        this.recordCancelCallback = null;
        this.blurListener = function () {
            _this.stopRecordingAndSendEvents();
        };
        this.focusListener = function () {
            void _this.initialize();
        };
        this.stopRecordingEvents = function () {
            try {
                _this.recordCancelCallback && _this.recordCancelCallback();
                _this.recordCancelCallback = null;
            }
            catch (error) {
                var typedError = error;
                _this.loggerProvider.warn("Error occurred while stopping recording: ".concat(typedError.toString()));
            }
        };
        this.loggerProvider = new Logger();
    }
    SessionReplay.prototype.init = function (apiKey, options) {
        return returnWrapper(this._init(apiKey, options));
    };
    SessionReplay.prototype._init = function (apiKey, options) {
        return __awaiter(this, void 0, void 0, function () {
            var globalScope;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.config = new SessionReplayConfig(apiKey, options);
                        this.loggerProvider = this.config.loggerProvider;
                        this.identifiers = new SessionIdentifiers(options, this.loggerProvider);
                        this.eventsManager = new SessionReplayEventsManager({
                            config: this.config,
                        });
                        this.loggerProvider.log('Installing @amplitude/session-replay-browser.');
                        globalScope = getGlobalScope();
                        if (globalScope) {
                            globalScope.removeEventListener('blur', this.blurListener);
                            globalScope.removeEventListener('focus', this.focusListener);
                            globalScope.addEventListener('blur', this.blurListener);
                            globalScope.addEventListener('focus', this.focusListener);
                        }
                        if (!(globalScope && globalScope.document && globalScope.document.hasFocus())) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.initialize(true)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    SessionReplay.prototype.setSessionId = function (sessionId, deviceId) {
        if (!this.identifiers) {
            this.loggerProvider.error('Session replay init has not been called, cannot set session id.');
            return;
        }
        if (deviceId) {
            this.identifiers.deviceId = deviceId;
        }
        // use a consistent device id.
        var deviceIdForReplayId = this.getDeviceId();
        if (sessionId && deviceIdForReplayId) {
            this.identifiers.sessionReplayId = generateSessionReplayId(sessionId, deviceIdForReplayId);
        }
        else {
            this.loggerProvider.error('Must provide either session replay id or session id when starting a new session.');
            return;
        }
        this.stopRecordingAndSendEvents(this.identifiers.sessionId);
        this.identifiers.sessionId = sessionId;
        this.eventsManager && this.eventsManager.resetSequence();
        this.recordEvents();
    };
    SessionReplay.prototype.getSessionReplayDebugPropertyValue = function () {
        var apiKeyHash = '';
        if (this.config) {
            apiKeyHash = generateHashCode(this.config.apiKey).toString();
        }
        return JSON.stringify({
            appHash: apiKeyHash,
        });
    };
    SessionReplay.prototype.getSessionReplayProperties = function () {
        var _a;
        if (!this.config || !this.identifiers) {
            this.loggerProvider.error('Session replay init has not been called, cannot get session recording properties.');
            return {};
        }
        // If the user is in debug mode, ignore the focus handler when tagging events.
        // this is a common mishap when someone is developing locally and not seeing events getting tagged.
        var ignoreFocus = !!this.config.debugMode;
        var shouldRecord = this.getShouldRecord(ignoreFocus);
        if (shouldRecord) {
            var eventProperties = (_a = {},
                _a[DEFAULT_SESSION_REPLAY_PROPERTY] = this.identifiers.sessionReplayId ? this.identifiers.sessionReplayId : null,
                _a);
            if (this.config.debugMode) {
                eventProperties[SESSION_REPLAY_DEBUG_PROPERTY] = this.getSessionReplayDebugPropertyValue();
            }
            return eventProperties;
        }
        return {};
    };
    SessionReplay.prototype.stopRecordingAndSendEvents = function (sessionId) {
        var _a;
        this.stopRecordingEvents();
        var sessionIdToSend = sessionId || ((_a = this.identifiers) === null || _a === void 0 ? void 0 : _a.sessionId);
        var deviceId = this.getDeviceId();
        this.eventsManager &&
            sessionIdToSend &&
            deviceId &&
            this.eventsManager.sendEvents({ sessionId: sessionIdToSend, deviceId: deviceId });
    };
    SessionReplay.prototype.initialize = function (shouldSendStoredEvents) {
        var _a;
        if (shouldSendStoredEvents === void 0) { shouldSendStoredEvents = false; }
        return __awaiter(this, void 0, void 0, function () {
            var deviceId, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (!((_a = this.identifiers) === null || _a === void 0 ? void 0 : _a.sessionId)) {
                            this.loggerProvider.warn("Session is not being recorded due to lack of session id.");
                            return [2 /*return*/];
                        }
                        deviceId = this.getDeviceId();
                        _b = this.eventsManager &&
                            deviceId;
                        if (!_b) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.eventsManager.initialize({
                                sessionId: this.identifiers.sessionId,
                                shouldSendStoredEvents: shouldSendStoredEvents,
                                deviceId: deviceId,
                            })];
                    case 1:
                        _b = (_c.sent());
                        _c.label = 2;
                    case 2:
                        _b;
                        this.recordEvents();
                        return [2 /*return*/];
                }
            });
        });
    };
    SessionReplay.prototype.shouldOptOut = function () {
        var _a, _b;
        var identityStoreOptOut;
        if ((_a = this.config) === null || _a === void 0 ? void 0 : _a.instanceName) {
            var identityStore = getAnalyticsConnector(this.config.instanceName).identityStore;
            identityStoreOptOut = identityStore.getIdentity().optOut;
        }
        return identityStoreOptOut !== undefined ? identityStoreOptOut : (_b = this.config) === null || _b === void 0 ? void 0 : _b.optOut;
    };
    SessionReplay.prototype.getShouldRecord = function (ignoreFocus) {
        if (ignoreFocus === void 0) { ignoreFocus = false; }
        if (!this.identifiers || !this.config) {
            this.loggerProvider.error("Session is not being recorded due to lack of config, please call sessionReplay.init.");
            return false;
        }
        var globalScope = getGlobalScope();
        if (!ignoreFocus && globalScope && globalScope.document && !globalScope.document.hasFocus()) {
            if (this.identifiers.sessionId) {
                this.loggerProvider.log("Session ".concat(this.identifiers.sessionId, " temporarily not recording due to lack of browser focus."));
            }
            return false;
        }
        else if (this.shouldOptOut()) {
            if (this.identifiers.sessionId) {
                this.loggerProvider.log("Opting session ".concat(this.identifiers.sessionId, " out of recording due to optOut config."));
            }
            return false;
        }
        else if (!this.identifiers.sessionId) {
            this.loggerProvider.warn("Session is not being recorded due to lack of session id.");
            return false;
        }
        var isInSample = isSessionInSample(this.identifiers.sessionId, this.config.sampleRate);
        if (!isInSample) {
            this.loggerProvider.log("Opting session ".concat(this.identifiers.sessionId, " out of recording due to sample rate."));
        }
        return isInSample;
    };
    SessionReplay.prototype.getBlockSelectors = function () {
        var _a, _b;
        return (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.privacyConfig) === null || _b === void 0 ? void 0 : _b.blockSelector;
    };
    SessionReplay.prototype.recordEvents = function () {
        var _this = this;
        var _a;
        var shouldRecord = this.getShouldRecord();
        var sessionId = (_a = this.identifiers) === null || _a === void 0 ? void 0 : _a.sessionId;
        if (!shouldRecord || !sessionId) {
            return;
        }
        this.stopRecordingEvents();
        this.recordCancelCallback = record({
            emit: function (event) {
                var globalScope = getGlobalScope();
                if ((globalScope && globalScope.document && !globalScope.document.hasFocus()) || !_this.getShouldRecord()) {
                    _this.stopRecordingAndSendEvents();
                    return;
                }
                var eventString = JSON.stringify(event);
                var deviceId = _this.getDeviceId();
                deviceId && _this.eventsManager && _this.eventsManager.addEvent({ event: eventString, sessionId: sessionId, deviceId: deviceId });
            },
            packFn: pack,
            maskAllInputs: true,
            maskTextClass: MASK_TEXT_CLASS,
            blockClass: BLOCK_CLASS,
            // rrweb only exposes array type through its types, but arrays are also be supported. #class, ['#class', 'id']
            blockSelector: this.getBlockSelectors(),
            maskInputFn: maskInputFn,
            recordCanvas: false,
            errorHandler: function (error) {
                var typedError = error;
                _this.loggerProvider.warn('Error while recording: ', typedError.toString());
                return true;
            },
        });
    };
    SessionReplay.prototype.getDeviceId = function () {
        var _a, _b;
        var identityStoreDeviceId;
        if ((_a = this.config) === null || _a === void 0 ? void 0 : _a.instanceName) {
            var identityStore = getAnalyticsConnector(this.config.instanceName).identityStore;
            identityStoreDeviceId = identityStore.getIdentity().deviceId;
        }
        return identityStoreDeviceId || ((_b = this.identifiers) === null || _b === void 0 ? void 0 : _b.deviceId);
    };
    SessionReplay.prototype.getSessionId = function () {
        var _a;
        return (_a = this.identifiers) === null || _a === void 0 ? void 0 : _a.sessionId;
    };
    SessionReplay.prototype.flush = function (useRetry) {
        if (useRetry === void 0) { useRetry = false; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (this.eventsManager) {
                    return [2 /*return*/, this.eventsManager.flush(useRetry)];
                }
                return [2 /*return*/];
            });
        });
    };
    SessionReplay.prototype.shutdown = function () {
        var globalScope = getGlobalScope();
        if (globalScope) {
            globalScope.removeEventListener('blur', this.blurListener);
            globalScope.removeEventListener('focus', this.focusListener);
        }
        this.stopRecordingAndSendEvents();
    };
    return SessionReplay;
}());
export { SessionReplay };
//# sourceMappingURL=session-replay.js.map